import { css } from '@emotion/core';
import styled from '@emotion/styled';
import {
  Avatar,
  List as MUIList,
  ListItemIcon,
  ListItemText as _ListItemText,
  MenuItem as _MenuItem,
} from '@material-ui/core';
import { List } from 'immutable';
import { ClassAttributes, createElement, SyntheticEvent } from 'react';

import YoutubeChannel from '../../../entity/YoutubeChannel';
import theme from '../../../theme';
import { SPACING_REGULAR } from '../../constant/spacing';
import { flexibleLayoutContainer } from '../../styleUtility/flexibleLayout';
import mq from '../../styleUtility/mediaquery';

interface Props {
  youtubeChannels: List<YoutubeChannel>;
  selected: YoutubeChannel | null;
  onRequestSelectChannel: (e: SyntheticEvent<HTMLElement>, selected: YoutubeChannel) => void;
  className?: string;
}

const YoutubeChannelList = ({
  youtubeChannels,
  selected,
  onRequestSelectChannel,
  className,
}: ClassAttributes<HTMLElement> & Props) => {
  if (youtubeChannels.size === 0) {
    return null;
  }

  const selectedListItemPrimaryStyle = css({
    color: theme.palette.primary.main,
  }).toString();

  const selectedListItemSecondaryStyle = css({
    color: theme.palette.primary.light,
  }).toString();

  const accounts = youtubeChannels.map((channel) => {
    const isSelected = selected ? selected.channelId === channel.channelId : false;

    const thumbnail = (
      <ListItemIcon>
        <Avatar src={channel.thumbnailUrl}>{channel.name.toUpperCase().charAt(0)}</Avatar>
      </ListItemIcon>
    );

    const channelName = isSelected ? (
      <ListItemText
        disableTypography
        primary={channel.name}
        classes={{
          primary: selectedListItemPrimaryStyle,
          secondary: selectedListItemSecondaryStyle,
        }}
      />
    ) : (
      <ListItemText disableTypography primary={channel.name} />
    );

    return (
      <MenuItem selected={isSelected} key={channel.channelId} onClick={(e) => onRequestSelectChannel(e, channel)}>
        {thumbnail}
        {channelName}
      </MenuItem>
    );
  });

  return <Root className={className}>{accounts}</Root>;
};

const Root = styled(MUIList)({
  height: '100%',
  maxHeight: '512px',
  overflowY: 'scroll',
  border: `1px solid ${theme.palette.grey['200']}`,
  [mq('sp')]: {
    maxHeight: 'none',
    overflowY: 'auto',
  },
});

const MenuItem = styled(_MenuItem)(
  flexibleLayoutContainer({
    direction: 'row',
    wrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'space-between',
  }),
  {
    height: 48,
    [mq('sp')]: {
      height: 'auto',
    },
  },
);

const ListItemText = styled(_ListItemText)({
  marginRight: SPACING_REGULAR,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  [mq('sp')]: {
    whiteSpace: 'initial',
    wordBreak: 'break-word',
    lineHeight: 1.1,
  },
});

export default YoutubeChannelList;
